import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Button, Typography, Grid } from "@mui/material";
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-mui";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { fetchCoursePackages } from "../../../redux/actions/contentCoursesActions";
import { makeGetEntities } from "../../../redux/selectors/selectors";
import Modal from "../../../components/Modal";
import Select from "../../../components/FormikFields/Select";
import {
  courseTypesForAdmin,
  courseTypes,
  trainingMinutesOptions
} from "../../../constants";

const selector = makeGetEntities();

const minuteMenuItems = trainingMinutesOptions.map(option => ({
  value: option,
  label: option
}));

const descriptionMaxCharacters = 800;

const getNewCourseSchema = t =>
  Yup.object().shape({
    name: Yup.string().required(t("validationMessages.nameRequired")),
    durationHours: Yup.number()
      .typeError(t("validationMessages.validNumber"))
      .min(0, t("validationMessages.validNumber"))
      .required(t("validationMessages.fieldRequired")),
    courseType: Yup.string().required(
      t("validationMessages.courseTypeRequired")
    ),
    coursePackage: Yup.string().required(
      t("validationMessages.coursePackageRequired")
    ),
    score: Yup.number().when("recordScore", {
      is: "yes",
      then: Yup.number()
        .typeError(t("validationMessages.validNumber"))
        .min(0, t("validationMessages.numberWithinRange", { min: 0, max: 100 }))
        .max(
          100,
          t("validationMessages.numberWithinRange", { min: 0, max: 100 })
        )
        .required(t("validationMessages.fieldRequired"))
    }),
    description: Yup.string().max(
      800,
      t("validationMessages.cannotExceedLength", {
        type: t("manageCourses.addCourseDescriptionField"),
        maxLength: 800
      })
    )
  });

const AddNewCourseForm = ({ packages, values, open, resetForm }) => {
  const { t } = useTranslation();

  useEffect(() => {
    resetForm();
  }, [open]);

  return (
    <Form>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Field
            variant="filled"
            fullWidth
            label={t("manageCourses.addCourseNameField")}
            component={TextField}
            name="name"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Field
            variant="filled"
            fullWidth
            label={t("manageCourses.addCourseHoursField")}
            component={TextField}
            name="durationHours"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Select
            name="durationMinutes"
            menuItems={minuteMenuItems}
            label={t("manageCourses.addCourseMinutesField")}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <Select
            name="courseType"
            menuItems={[
              {
                value: courseTypes.online.toLowerCase(),
                label: t("manageCourses.addCourseTypeOnline")
              },
              {
                value: courseTypes.ilt.toLowerCase(),
                label: t("manageCourses.addCourseTypeIlt")
              }
            ]}
            label={t("manageCourses.addCourseTypeField")}
          />
        </Grid>
        <Grid item xs={12} sm={7}>
          <Select
            name="recordScore"
            menuItems={[
              {
                value: "yes",
                label: t("manageCourses.addCourseRecordScoreYes")
              },
              {
                value: "no",
                label: t("manageCourses.addCourseRecordScoreNo")
              }
            ]}
            label={t("manageCourses.addCourseRecordScoreField")}
          />
        </Grid>
        {values.recordScore === "yes" && (
          <Grid item xs={12} sm={5}>
            <Field
              variant="filled"
              fullWidth
              label={t("manageCourses.addCourseScoreField")}
              component={TextField}
              name="score"
            />
          </Grid>
        )}
        <Grid item xs={12} sm={7}>
          <Select
            name="coursePackage"
            menuItems={
              packages
                ? packages.map(coursePackage => ({
                    value: coursePackage.id,
                    label: coursePackage.name
                  }))
                : []
            }
            label={t("manageCourses.addCoursePackageField")}
          />
        </Grid>
        <Grid item xs={12}>
          <Field
            variant="filled"
            fullWidth
            label={t("manageCourses.addCourseDescriptionField")}
            component={TextField}
            name="description"
            multiline
            helperText={`${descriptionMaxCharacters -
              values.description.length}/${descriptionMaxCharacters} ${t(
              "manageCourses.addCourseDescriptionHelperText"
            )}`}
            rows={4}
          />
        </Grid>
      </Grid>
    </Form>
  );
};

AddNewCourseForm.propTypes = {
  packages: PropTypes.any,
  values: PropTypes.any
};

function AddNewCourseModal(props) {
  const { open, onClose, onSubmitCourse } = props;
  const { t } = useTranslation();

  const { packages } = useSelector(({ contentCoursesState }) => ({
    packages: selector(contentCoursesState, {
      type: "coursePackage"
    })
  }));

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchCoursePackages());
  }, []);

  const customPackage = !!packages && packages.length > 0 && packages[0].id;

  return (
    <Formik
      initialValues={{
        name: "",
        durationHours: 0,
        durationMinutes: 0,
        courseType: courseTypesForAdmin.online,
        recordScore: "no",
        score: 70,
        coursePackage: customPackage,
        description: ""
      }}
      validationSchema={getNewCourseSchema(t)}
      enableReinitialize
      onSubmit={values => onSubmitCourse(values)}
    >
      {({ submitForm, isSubmitting, values, resetForm }) => (
        <Modal
          scrollable
          maxWidth="md"
          fullWidth
          dialogTitle={
            <Typography variant="h4" color="textPrimary">
              {t("manageCourses.addCourseModalTitle")}
            </Typography>
          }
          dialogContent={
            <AddNewCourseForm
              packages={packages}
              values={values}
              resetForm={resetForm}
              open={open}
            />
          }
          dialogActions={
            <>
              <Button color="primary" disabled={isSubmitting} onClick={onClose}>
                {t("manageCourses.addCourseCancelButton")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={submitForm}
                disabled={isSubmitting}
              >
                {t("manageCourses.addCourseSubmitButton")}
              </Button>
            </>
          }
          open={open}
          onClose={onClose}
          showCloseIcon
        />
      )}
    </Formik>
  );
}

AddNewCourseModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired
};

export default AddNewCourseModal;
